import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import { useAuthStore } from '../store/useAuthStore';
import Header from '../components/layout/Header';
import { 
  TrendingUp, 
  TrendingDown, 
  RefreshCw, 
  Calendar, 
  ArrowLeft, 
  DollarSign, 
  TrendingUp as EurIcon,
  CheckCircle2, 
  AlertCircle 
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend 
} from 'recharts';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

dayjs.locale('es');

export default function BcvSettings() {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const isManager = ['admin', 'manager', 'super_admin'].includes(user?.role);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [currentRate, setCurrentRate] = useState(null);
    const [history, setHistory] = useState([]);
    const [message, setMessage] = useState({ type: '', text: '' });

    const [showManualModal, setShowManualModal] = useState(false);
    const [manualForm, setManualForm] = useState({ date: dayjs().format('YYYY-MM-DD'), usd: '', eur: '' });
    const [savingManual, setSavingManual] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [currentRes, historyRes] = await Promise.all([
                axios.get('/api/bcv-rates/current').catch(() => null),
                axios.get('/api/bcv-rates/history').catch(() => ({ data: [] }))
            ]);

            if (currentRes && currentRes.data) {
                setCurrentRate(currentRes.data);
            }
            if (historyRes && historyRes.data) {
                setHistory(historyRes.data);
            }
        } catch (error) {
            console.error('Error fetching BCV rates:', error);
            setMessage({ type: 'error', text: 'No se pudieron cargar los datos de las tasas cambiarias.' });
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        if (!isManager) return;
        setRefreshing(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await axios.post('/api/bcv-rates/refresh');
            setCurrentRate(response.data.rate);
            setMessage({ type: 'success', text: response.data.message });
            
            // Reload history to include the updated data
            const historyRes = await axios.get('/api/bcv-rates/history');
            setHistory(historyRes.data);
        } catch (error) {
            console.error('Error refreshing BCV rates:', error);
            setMessage({ 
                type: 'error', 
                text: error.response?.data?.message || 'Error al intentar actualizar la tasa cambiaria.' 
            });
        } finally {
            setRefreshing(false);
        }
    };

    const handleManualSubmit = async (e) => {
        e.preventDefault();
        if (!manualForm.usd || !manualForm.eur || !manualForm.date) {
            alert('Por favor completa todos los campos.');
            return;
        }
        setSavingManual(true);
        setMessage({ type: '', text: '' });
        try {
            const response = await axios.post('/api/bcv-rates', {
                date: manualForm.date,
                usd: parseFloat(manualForm.usd),
                eur: parseFloat(manualForm.eur)
            });
            
            setMessage({ type: 'success', text: response.data.message });
            setShowManualModal(false);
            setManualForm({ date: dayjs().format('YYYY-MM-DD'), usd: '', eur: '' });
            
            // Reload current and history
            const [currentRes, historyRes] = await Promise.all([
                axios.get('/api/bcv-rates/current').catch(() => null),
                axios.get('/api/bcv-rates/history').catch(() => ({ data: [] }))
            ]);
            if (currentRes && currentRes.data) setCurrentRate(currentRes.data);
            if (historyRes && historyRes.data) setHistory(historyRes.data);
        } catch (error) {
            console.error('Error saving manual rate:', error);
            alert(error.response?.data?.message || 'Error al guardar la tasa manual.');
        } finally {
            setSavingManual(false);
        }
    };

    // Format chart data chronologically
    const chartData = [...history]
        .reverse()
        .map(item => ({
            date: dayjs(item.date).format('DD MMM'),
            Dólar: parseFloat(item.usd),
            Euro: parseFloat(item.eur)
        }));

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('es-VE', {
            style: 'currency',
            currency: 'VES',
            minimumFractionDigits: 4,
            maximumFractionDigits: 4
        }).format(val);
    };

    const formatPercentage = (val) => {
        if (val === null || val === undefined) return '0.00%';
        const num = parseFloat(val);
        const sign = num > 0 ? '+' : '';
        return `${sign}${num.toFixed(2)}%`;
    };

    const renderChangeBadge = (val) => {
        if (val === null || val === undefined) return null;
        const num = parseFloat(val);
        const isPositive = num > 0;
        const isZero = num === 0;

        if (isZero) {
            return (
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                    0.00%
                </span>
            );
        }

        return (
            <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-0.5 rounded-full ${
                isPositive 
                    ? 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400' 
                    : 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400'
            }`}>
                {isPositive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {formatPercentage(num)}
            </span>
        );
    };

    return (
        <div className="flex flex-col h-full bg-slate-50 dark:bg-background-dark">
            <Header title="Tasa Cambiaria BCV" />
            
            <div className="flex-1 overflow-y-auto p-4 md:p-8 no-scrollbar">
                <div className="max-w-5xl mx-auto space-y-6">
                    
                    {/* Back Button */}
                    <button 
                        onClick={() => navigate('/settings')} 
                        className="group flex items-center gap-2 text-sm font-semibold text-text-secondary hover:text-text-main dark:hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        Volver a Configuración
                    </button>

                    {/* Messages */}
                    {message.text && (
                        <div className={`p-4 rounded-xl border flex items-center gap-3 animate-in fade-in duration-300 ${
                            message.type === 'error' 
                                ? 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900/30' 
                                : 'bg-green-50 text-green-800 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900/30'
                        }`}>
                            {message.type === 'error' ? <AlertCircle className="w-5 h-5 shrink-0 text-red-600 dark:text-red-400" /> : <CheckCircle2 className="w-5 h-5 shrink-0 text-green-600 dark:text-green-400" />}
                            <span className="text-sm font-medium">{message.text}</span>
                            <button 
                                onClick={() => setMessage({ type: '', text: '' })} 
                                className="ml-auto text-xl font-bold opacity-60 hover:opacity-100"
                            >&times;</button>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <RefreshCw className="w-10 h-10 animate-spin text-primary" />
                            <p className="text-sm text-text-secondary mt-4 font-semibold">Cargando datos cambiarios...</p>
                        </div>
                    ) : (
                        <>
                            {/* Current Rates Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                
                                {/* USD Card */}
                                <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full -mr-8 -mt-8 transition-transform group-hover:scale-110 duration-500" />
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl text-emerald-600 dark:text-emerald-400">
                                            <DollarSign className="w-6 h-6" />
                                        </div>
                                        {currentRate && renderChangeBadge(currentRate.change_percentage_usd)}
                                    </div>
                                    <p className="text-xs font-bold text-text-secondary uppercase tracking-wider">Tasa Dólar BCV (USD)</p>
                                    <h3 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight mt-1">
                                        {currentRate ? formatCurrency(currentRate.usd) : 'N/D'}
                                    </h3>
                                    <div className="flex items-center gap-1.5 text-xs text-text-secondary mt-4">
                                        <Calendar className="w-3.5 h-3.5" />
                                        <span>Fecha Oficial: {currentRate ? dayjs(currentRate.date).format('DD [de] MMMM, YYYY') : 'Desconocida'}</span>
                                    </div>
                                </div>

                                {/* EUR Card */}
                                <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 rounded-full -mr-8 -mt-8 transition-transform group-hover:scale-110 duration-500" />
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-xl text-blue-600 dark:text-blue-400">
                                            <span className="text-xl font-bold font-serif">€</span>
                                        </div>
                                        {currentRate && renderChangeBadge(currentRate.change_percentage_eur)}
                                    </div>
                                    <p className="text-xs font-bold text-text-secondary uppercase tracking-wider">Tasa Euro BCV (EUR)</p>
                                    <h3 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight mt-1">
                                        {currentRate ? formatCurrency(currentRate.eur) : 'N/D'}
                                    </h3>
                                    <div className="flex items-center gap-1.5 text-xs text-text-secondary mt-4">
                                        <Calendar className="w-3.5 h-3.5" />
                                        <span>Fecha Oficial: {currentRate ? dayjs(currentRate.date).format('DD [de] MMMM, YYYY') : 'Desconocida'}</span>
                                    </div>
                                </div>

                                {/* Sync Status / Action Card */}
                                <div className="bg-gradient-to-br from-slate-900 to-slate-800 dark:from-[#1a100c]/30 dark:to-surface-dark border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-md flex flex-col justify-between text-white">
                                    <div>
                                        <h4 className="font-bold text-lg mb-1 tracking-tight text-white">Estado de Sincronización</h4>
                                        <p className="text-xs text-gray-300 leading-relaxed">
                                            El sistema se actualiza automáticamente dos veces al día. Si necesitas la tasa del momento de inmediato, puedes forzar el refresco.
                                        </p>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-white/10 flex flex-col gap-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-gray-400 uppercase font-bold">Último chequeo</span>
                                                <span className="text-xs font-semibold text-emerald-400">
                                                    {currentRate ? dayjs(currentRate.updated_at).format('DD/MM/YYYY HH:mm') : 'Nunca'}
                                                </span>
                                            </div>
                                            {isManager && (
                                                <button 
                                                    onClick={handleRefresh}
                                                    disabled={refreshing}
                                                    className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs py-2.5 px-4 rounded-xl shadow-sm transition-all disabled:opacity-50"
                                                >
                                                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                                                    {refreshing ? 'Actualizar API' : 'Actualizar API'}
                                                </button>
                                            )}
                                        </div>
                                        {isManager && (
                                            <button 
                                                onClick={() => setShowManualModal(true)}
                                                className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-xs py-2 px-4 rounded-xl transition-all animate-in slide-in-from-bottom duration-300"
                                            >
                                                <span className="material-symbols-outlined text-sm">edit_note</span>
                                                Registrar Tasa Manual
                                            </button>
                                        )}
                                    </div>
                                </div>

                            </div>

                            {/* Line Chart Card */}
                            {chartData.length > 0 && (
                                <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm">
                                    <div className="mb-4">
                                        <h3 className="text-base font-bold text-text-main dark:text-white">Tendencia de Tasa BCV (Últimos 30 días)</h3>
                                        <p className="text-xs text-text-secondary mt-0.5">Historial comparativo de fluctuación de la divisa en bolívares.</p>
                                    </div>
                                    <div className="h-[320px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.5} />
                                                <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} />
                                                <YAxis stroke="#94a3b8" fontSize={11} domain={['auto', 'auto']} tickLine={false} />
                                                <Tooltip 
                                                    contentStyle={{ 
                                                        backgroundColor: '#0f172a', 
                                                        borderColor: '#1e293b', 
                                                        borderRadius: '12px',
                                                        color: '#fff',
                                                        fontSize: '12px'
                                                    }} 
                                                />
                                                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                                                <Line type="monotone" dataKey="Dólar" stroke="#10b981" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                                                <Line type="monotone" dataKey="Euro" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            )}

                            {/* History Table */}
                            <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm overflow-hidden">
                                <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                                    <h3 className="text-base font-bold text-text-main dark:text-white">Historial de Tasas Cambiarias</h3>
                                    <p className="text-xs text-text-secondary mt-0.5">Lista cronológica del valor oficial publicado por el Banco Central.</p>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm text-text-main dark:text-gray-300">
                                        <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs font-bold text-text-secondary uppercase">
                                            <tr>
                                                <th className="px-6 py-4">Fecha Oficial</th>
                                                <th className="px-6 py-4">Valor USD</th>
                                                <th className="px-6 py-4">Variación USD</th>
                                                <th className="px-6 py-4">Valor EUR</th>
                                                <th className="px-6 py-4">Variación EUR</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                            {history.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="px-6 py-10 text-center text-gray-400">
                                                        No hay registros históricos disponibles.
                                                    </td>
                                                </tr>
                                            ) : (
                                                history.map((row) => (
                                                    <tr key={row.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                                                        <td className="px-6 py-4 font-semibold">
                                                            {dayjs(row.date).format('DD [de] MMMM, YYYY')}
                                                        </td>
                                                        <td className="px-6 py-4 font-mono font-bold text-slate-800 dark:text-white">
                                                            {formatCurrency(row.usd)}
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            {renderChangeBadge(row.change_percentage_usd)}
                                                        </td>
                                                        <td className="px-6 py-4 font-mono font-bold text-slate-800 dark:text-white">
                                                            {formatCurrency(row.eur)}
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            {renderChangeBadge(row.change_percentage_eur)}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                        </>
                    )}

                </div>
            </div>

            {/* Manual Entry Modal */}
            {showManualModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setShowManualModal(false)} />
                    <div className="relative z-10 bg-white dark:bg-surface-dark rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Registrar Tasa Cambiaria Manual</h3>
                            <p className="text-xs text-text-secondary mt-0.5">Ingresa los valores del día en bolívares de forma manual.</p>
                        </div>
                        <form onSubmit={handleManualSubmit}>
                            <div className="p-6 space-y-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-text-secondary uppercase">Fecha</label>
                                    <input 
                                        type="date"
                                        value={manualForm.date}
                                        onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
                                        className="w-full bg-white dark:bg-background-dark border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent dark:text-white"
                                        required
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-text-secondary uppercase">Tasa Dólar (USD)</label>
                                        <input 
                                            type="number"
                                            step="0.0001"
                                            min="0.0001"
                                            placeholder="ej. 727.4512"
                                            value={manualForm.usd}
                                            onChange={(e) => setManualForm({ ...manualForm, usd: e.target.value })}
                                            className="w-full bg-white dark:bg-background-dark border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent dark:text-white"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-text-secondary uppercase">Tasa Euro (EUR)</label>
                                        <input 
                                            type="number"
                                            step="0.0001"
                                            min="0.0001"
                                            placeholder="ej. 832.2478"
                                            value={manualForm.eur}
                                            onChange={(e) => setManualForm({ ...manualForm, eur: e.target.value })}
                                            className="w-full bg-white dark:bg-background-dark border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent dark:text-white"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex border-t border-gray-100 dark:border-gray-800">
                                <button
                                    type="button"
                                    onClick={() => setShowManualModal(false)}
                                    className="flex-1 px-4 py-4 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={savingManual}
                                    className="flex-1 px-4 py-4 text-sm font-bold text-white bg-primary hover:bg-primary/95 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {savingManual && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                                    Guardar Tasa
                                </button>
                            </div>
                        </form>
                     </div>
                 </div>
             )}
        </div>
    );
}
